import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ReportTemplateLibrary = ({ onSelectTemplate, onClose }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const templates = [
    {
      id: 'monthly-summary',
      name: 'Resumo Mensal Completo',
      description: 'Visão geral de receitas, despesas e lucros do mês com gráficos comparativos',
      category: 'financial',
      icon: 'Calendar',
      color: 'primary',
      frequency: 'Mensal',
      charts: ['bar', 'line', 'pie'],
      sections: ['Resumo Executivo', 'Gráficos', 'Tabelas Detalhadas']
    },
    {
      id: 'client-profitability',
      name: 'Análise de Lucratividade por Cliente',
      description: 'Detalhamento de receitas, custos e margens por cliente individual',
      category: 'clients',
      icon: 'Users',
      color: 'accent',
      frequency: 'Sob Demanda',
      charts: ['bar', 'line'],
      sections: ['Ranking de Clientes', 'Análise de Margem', 'Histórico']
    },
    {
      id: 'expense-breakdown',
      name: 'Detalhamento de Despesas',
      description: 'Análise completa de despesas por categoria com tendências e comparações',
      category: 'expenses',
      icon: 'Receipt',
      color: 'error',
      frequency: 'Mensal',
      charts: ['pie', 'bar'],
      sections: ['Distribuição por Categoria', 'Evolução Temporal', 'Top Despesas']
    },
    {
      id: 'year-comparison',
      name: 'Comparação Anual',
      description: 'Comparativo ano a ano de todos os indicadores financeiros principais',
      category: 'financial',
      icon: 'TrendingUp',
      color: 'success',
      frequency: 'Anual',
      charts: ['line', 'bar'],
      sections: ['Crescimento YoY', 'Análise de Tendências', 'Projeções']
    },
    {
      id: 'cash-flow-detailed',
      name: 'Fluxo de Caixa Detalhado',
      description: 'Análise completa de entradas e saídas com projeções futuras',
      category: 'financial',
      icon: 'DollarSign',
      color: 'primary',
      frequency: 'Semanal',
      charts: ['line', 'bar'],
      sections: ['Saldo Atual', 'Movimentações', 'Projeções']
    },
    {
      id: 'category-analysis',
      name: 'Análise por Categoria',
      description: 'Estudo aprofundado de performance por categoria de serviço',
      category: 'analysis',
      icon: 'BarChart3',
      color: 'accent',
      frequency: 'Trimestral',
      charts: ['pie', 'bar', 'line'],
      sections: ['Performance por Categoria', 'Comparativos', 'Insights']
    },
    {
      id: 'quarterly-review',
      name: 'Revisão Trimestral',
      description: 'Relatório executivo trimestral com KPIs e análise estratégica',
      category: 'financial',
      icon: 'FileText',
      color: 'primary',
      frequency: 'Trimestral',
      charts: ['bar', 'line'],
      sections: ['KPIs Principais', 'Análise Estratégica', 'Ações Recomendadas']
    },
    {
      id: 'tax-report',
      name: 'Relatório Fiscal',
      description: 'Compilação de dados para declarações fiscais e impostos',
      category: 'compliance',
      icon: 'FileCheck',
      color: 'warning',
      frequency: 'Mensal',
      charts: ['bar'],
      sections: ['Receitas Tributáveis', 'Deduções', 'Resumo Fiscal']
    }
  ];

  const categories = [
    { id: 'all', label: 'Todos', icon: 'Grid' },
    { id: 'financial', label: 'Financeiro', icon: 'DollarSign' },
    { id: 'clients', label: 'Clientes', icon: 'Users' },
    { id: 'expenses', label: 'Despesas', icon: 'Receipt' },
    { id: 'analysis', label: 'Análise', icon: 'BarChart3' },
    { id: 'compliance', label: 'Compliance', icon: 'Shield' }
  ];

  const filteredTemplates = selectedCategory === 'all' 
    ? templates 
    : templates?.filter(t => t?.category === selectedCategory);

  const getColorClass = (color) => {
    const colorMap = {
      primary: 'bg-primary/10 text-primary',
      accent: 'bg-accent/10 text-accent',
      success: 'bg-success/10 text-success',
      error: 'bg-error/10 text-error',
      warning: 'bg-warning/10 text-warning'
    };
    return colorMap?.[color] || colorMap?.primary;
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[1030] flex items-center justify-center p-4">
      <div className="bg-card rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-border flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Biblioteca de Templates</h2>
            <p className="text-sm text-muted-foreground mt-1">Selecione um template pré-configurado para começar</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-md hover:bg-muted transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-6 border-b border-border">
          <div className="flex items-center gap-2 overflow-x-auto">
            {categories?.map((category) => (
              <button
                key={category?.id}
                onClick={() => setSelectedCategory(category?.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                  selectedCategory === category?.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                <Icon name={category?.icon} size={16} />
                {category?.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredTemplates?.map((template) => (
              <div
                key={template?.id}
                className="bg-background border border-border rounded-lg p-4 hover:border-primary transition-all cursor-pointer group"
                onClick={() => onSelectTemplate(template)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${getColorClass(template?.color)}`}>
                    <Icon name={template?.icon} size={24} />
                  </div>
                  <span className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground">
                    {template?.frequency}
                  </span>
                </div>

                <h3 className="text-base font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                  {template?.name}
                </h3>
                <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                  {template?.description}
                </p>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2">
                    <Icon name="BarChart" size={14} className="text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">
                      {template?.charts?.length} tipos de gráficos
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="Layers" size={14} className="text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">
                      {template?.sections?.length} seções
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-border">
                  <span className="text-xs text-muted-foreground">Clique para usar</span>
                  <Icon name="ArrowRight" size={16} className="text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="p-6 border-t border-border">
          <Button variant="outline" fullWidth onClick={onClose}>
            Fechar
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReportTemplateLibrary;